import { useCallback, type Dispatch, type SetStateAction } from 'react'
import type { AuthState, LoginRequest } from '../../types'
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  isAuthenticated as checkAuth,
} from '../../services/auth'

export interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  clearPasswordMustChange: () => void
}

const UNAUTHENTICATED: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,
}

export function useAuthActions(setState: Dispatch<SetStateAction<AuthState>>): AuthActions {
  const login = useCallback(
    async (credentials: LoginRequest): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true }))
      try {
        const response = await apiLogin(credentials)
        setState({
          user: response.user,
          accessToken: response.accessToken,
          isAuthenticated: true,
          isLoading: false,
        })
      } catch (error) {
        setState((prev) => ({ ...prev, isLoading: false }))
        throw error
      }
    },
    [setState]
  )

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout()
    } finally {
      setState(UNAUTHENTICATED)
    }
  }, [setState])

  const refreshUser = useCallback(async (): Promise<void> => {
    if (!checkAuth()) return
    try {
      const user = await getCurrentUser()
      setState((prev) => ({ ...prev, user }))
    } catch {
      // Ignore errors during refresh
    }
  }, [setState])

  const clearPasswordMustChange = useCallback((): void => {
    setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, passwordMustChange: false } : null,
    }))
  }, [setState])

  return { login, logout, refreshUser, clearPasswordMustChange }
}
